<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue';
import type { TPercentageByType } from '@/_core/entities/report/models/types';
import type { TVulnerabilityDetails } from '@/_core/entities/vulnerability/models/types';
import { useDownloadVulnerabilityImage } from '@/_core/entities/vulnerability/api/useDownloadVulnerabilityImage';
import VulnerabilitySeverity from '@/_core/entities/vulnerability/ui/VulnerabilitySeverity/VulnerabilitySeverity.vue';
import ReportSection from '@/_core/entities/report/ui/ReportSection/ReportSection.vue';
import ReportActionButton from '@/_core/entities/report/ui/ReportActionButton/ReportActionButton.vue';
import LinkIcon from '@/assets/images/svg/link.svg?component';
import CheckIcon from '@/assets/images/svg/check.svg?component';
import FalsePositiveIcon from '@/assets/images/svg/false-positive-can.svg?component';
import DownloadPicIcon from '@/assets/images/svg/download-pic.svg?component';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';
import FoldersDropdown from '@/_core/entities/vulnerability/ui/FoldersDropdown/FoldersDropdown.vue';
import CreateFolderModal from '@/_core/entities/vulnerability/ui/CreateFolderModal/CreateFolderModal.vue';
import type { TFolder } from '@/_core/entities/report/models/types';
import { useFalsePositiveFolder } from '@/_core/entities/vulnerability/utils/useFalsePositiveFolder';
import { DEFAULT_FOLDER_NAMES } from '@/_core/entities/report/models/consts';

const props = defineProps<{
  categoryPercentageMap: Record<string, TPercentageByType>;
  vulnerability: TVulnerabilityDetails;
  folders: TFolder[];
  areFoldersLoading: boolean;
  hasFoldersError: boolean;
  isCreateFolderModalOpen: boolean;
  isCreateFolderLoading: boolean;
  isMovingVulnToFolder: boolean;
  isFetchingVulnerability: boolean;
}>();

const emit = defineEmits<{
  (e: 'open-create-folder-modal'): void;
  (e: 'close-create-folder-modal'): void;
  (e: 'create-folder', folderName: string): void;
  (e: 'move', folder: TFolder): void;
}>();

const instance = getCurrentInstance();
const route = computed(() => instance?.proxy.$route);

const categoryEntry = computed(() => props.categoryPercentageMap[props.vulnerability.categoryId]);

const links = computed<string[]>(() => {
  if (!categoryEntry.value?.links) return [];
  try {
    return JSON.parse(categoryEntry.value.links);
  } catch {
    return [];
  }
});

const toast = useToast();

const isCopied = ref(false);

async function copyLink() {
  await navigator.clipboard.writeText(window.location.href);
  isCopied.value = true;
  setTimeout(() => (isCopied.value = false), 2000);
}

const falsePositiveFolder = useFalsePositiveFolder(computed(() => props.folders));

const { mutateAsync: downloadImage, isPending: isDownloading } = useDownloadVulnerabilityImage();

async function handleDownloadImageClick() {
  const { uid, jobId, categoryId } = props.vulnerability;
  const params = route.value?.params;
  const NEW_TAB_TARGET = '_blank';

  try {
    if (!params) {
      return toast.error({
        message: 'Failed to download vulnerability image. Missing parameters.',
      });
    }

    if (params.jobId) {
      const data = await downloadImage({
        type: 'single-scan',
        jobId,
        vulnerabilityId: uid,
        categoryId,
      });

      window.open(data.url, NEW_TAB_TARGET);
      return;
    }

    const data = await downloadImage({
      type: 'app-version',
      integrationId: params.integrationId,
      branchName: params.branchName,
      versionId: params.versionId,
      vulnerabilityId: uid,
      categoryId,
    });

    window.open(data.url, NEW_TAB_TARGET);
  } catch (errInstance) {
    console.error(errInstance);

    const hasErrorMsg =
      errInstance &&
      typeof errInstance === 'object' &&
      errInstance !== null &&
      'message' in errInstance &&
      typeof errInstance.message === 'string';

    toast.error({
      message: hasErrorMsg ? (errInstance.message as string) : 'An unexpected error occurred',
    });
  }
}
</script>

<template>
  <div>
    <ReportSection>
      <div class="tw:flex tw:max-md:flex-col tw:gap-5 tw:justify-between">
        <div class="tw:flex-1">
          <div class="tw:flex tw:items-center tw:gap-4">
            <VulnerabilitySeverity :severity="vulnerability.category.severity" postfix="severity" />

            <div class="tw:truncate tw:max-md:max-w-54 tw:typography-small tw:text-secondary">
              {{ vulnerability.uid }}
            </div>
          </div>

          <div class="tw:mt-5 tw:lg:mt-4 tw:typography-h1 tw:max-w-200">
            {{ vulnerability.category.descriptionTitle }}
          </div>
        </div>

        <div class="tw:flex-1 tw:flex tw:gap-5 tw:max-sm:flex-col tw:md:gap-2 tw:justify-end">
          <div class="tw:flex-1 tw:lg:max-w-60" v-if="!hasFoldersError">
            <div
              v-if="areFoldersLoading || isMovingVulnToFolder || isFetchingVulnerability"
              class="tw:h-10 tw:rounded-lg tw:skeleton-shimmer-animation"
            />
            <FoldersDropdown
              v-else
              :folders="folders"
              :current-folder="vulnerability.folder"
              className="tw:flex-1"
              @move="emit('move', $event)"
              @create-folder="emit('open-create-folder-modal')"
            />
          </div>

          <div class="tw:flex tw:gap-2">
            <ReportActionButton
              :icon="isCopied ? CheckIcon : LinkIcon"
              :label="isCopied ? 'Link copied' : undefined"
              @click="copyLink"
            />

            <ReportActionButton
              v-if="
                falsePositiveFolder &&
                vulnerability.folder?.name !== DEFAULT_FOLDER_NAMES.falsePositives
              "
              :icon="FalsePositiveIcon"
              :disabled="!falsePositiveFolder"
              @click="falsePositiveFolder && emit('move', falsePositiveFolder)"
            />
            <ReportActionButton
              :icon="DownloadPicIcon"
              :loading="isDownloading"
              @click="handleDownloadImageClick"
            />
          </div>
        </div>
      </div>

      <div
        class="tw:mt-6 tw:lg:mt-8 tw:flex tw:max-lg:flex-col tw:gap-6 tw:lg:gap-12"
        v-if="categoryEntry"
      >
        <div class="tw:flex tw:flex-col tw:gap-2 tw:grow tw:min-w-0 tw:basis-0">
          <div class="tw:typography-h2">Vulnerability Description</div>

          <div class="tw:typography-small tw:wrap-break-word tw:text-secondary">
            {{ categoryEntry.text }}
          </div>
        </div>

        <div class="tw:flex tw:flex-col tw:gap-2 tw:grow tw:min-w-0 tw:basis-0">
          <div class="tw:typography-h2">Remediation</div>

          <div class="tw:typography-small tw:wrap-break-word tw:text-secondary">
            {{ categoryEntry.remediation }}
          </div>
        </div>

        <div class="tw:flex tw:flex-col tw:gap-2 tw:grow tw:min-w-0 tw:basis-0">
          <div class="tw:typography-h2">Links</div>

          <div class="tw:flex tw:flex-col tw:gap-0.5">
            <a
              v-for="item in links"
              :key="item"
              rel="noopener nofollow"
              :href="item"
              target="_blank"
              class="tw:typography-small tw:text-main-blue tw:font-medium"
              >{{ item }}</a
            >
          </div>
        </div>
      </div>
    </ReportSection>

    <CreateFolderModal
      v-if="isCreateFolderModalOpen"
      :is-loading="isCreateFolderLoading || isFetchingVulnerability"
      :close-modal="() => emit('close-create-folder-modal')"
      :on-create-folder="(name) => emit('create-folder', name)"
    />
  </div>
</template>
